import { useToast } from '@chakra-ui/react';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type UserLocationProviderProps = {
  children: ReactNode;
};

type UserLocationContextType = {
  latitude?: number;
  longitude?: number;
  error?: string;
  setLatitude?: React.Dispatch<React.SetStateAction<number | undefined>>;
  setLongitude?: React.Dispatch<React.SetStateAction<number | undefined>>;
};

export const UserLocationContext = createContext<UserLocationContextType>({});

export const useUserLocation = () => {
  const context = useContext(UserLocationContext);

  if (!context) {
    throw new Error(
      'useUserLocation must be used within a UserLocationProvider',
    );
  }

  return context;
};

export const UserLocationProvider: React.FC<UserLocationProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [latitude, setLatitude] = useState<number>();
  const [longitude, setLongitude] = useState<number>();
  const [error, setError] = useState<string>();
  const toast = useToast();

  useEffect(() => {
    // Check if the browser supports Geolocation
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Set the latitude and longitude from the position object
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
        },
        (getPositionError) => {
          setLatitude(47.4979);
          setLongitude(19.0402);

          if (!error) {
            toast({
              title: t('warnings.denied_location_access_title'),
              description: t('warnings.denied_location_access_description'),
              status: 'warning',
              duration: 9000,
              isClosable: true,
              position: 'top',
            });
          }
          setError(getPositionError.message);
        },
      );
    }
  }, [error, t, toast]);

  const ctx = useMemo(
    () => ({ latitude, setLatitude, longitude, setLongitude, error }),
    [error, latitude, longitude],
  );

  return (
    <UserLocationContext.Provider value={ctx}>
      {children}
    </UserLocationContext.Provider>
  );
};
